import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/auth.store';

/**
 * Reactive gate on Zustand persist hydration for the auth store.
 *
 * Zustand v5 rehydrates persisted storage in a microtask — the first
 * React render therefore sees the DEFAULT auth state (empty user,
 * `isAuthenticated=false`) even when a valid session sits on disk.
 * Guards that redirect on `!isAuthenticated` would fire once, land
 * the browser on `/auth/login`, then silently see the hydrated
 * session AFTER the route unmounted. For a sadmin hard-refresh the
 * operator visibly bounces through the login surface.
 *
 * This hook reads `useAuthStore.persist.hasHydrated()` (built-in
 * middleware API — no state mutation, no persist write) and
 * subscribes to `onFinishHydration` so a component re-renders the
 * moment hydration completes. Guards use it to render a loader
 * until the persisted session (or lack of it) is actually known.
 *
 * Never write to `useAuthStore` from a fallback here — a race with
 * hydration's own `set()` will wipe the persisted blob (previous
 * `_hasHydrated`-flag attempt shipped that exact regression).
 */
export function useAuthHydrated(): boolean {
  const [hydrated, setHydrated] = useState<boolean>(() =>
    useAuthStore.persist.hasHydrated()
  );
  useEffect(() => {
    // Re-check in case hydration completed between the initial
    // render and the effect firing.
    if (useAuthStore.persist.hasHydrated()) {
      setHydrated(true);
      return;
    }
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      setHydrated(true);
    });
    return unsub;
  }, []);
  return hydrated;
}
